import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "AgendasdeViaje";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const ListarAgendasViatico = ({ refresh }) => {
  const [agendas, setAgendas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);

  const navigate = useNavigate();
  const { id } = useParams();

  // ==============================
  // CONFIG TOKEN
  // ==============================
  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // ==============================
  // PERMISOS MÓDULO AGENDAS DE VIAJE
  // ==============================
  const getModuloAgendas = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
          ? JSON.parse(usuarioStorage)
          : userStorage
            ? JSON.parse(userStorage)
            : usuariosStorage
              ? JSON.parse(usuariosStorage)
              : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo ||
            modulo?.modulo ||
            modulo?.nombre ||
            "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloAgendas = useMemo(() => getModuloAgendas(), []);

  const puedeCrear =
    moduloAgendas?.puedeCrear === true ||
    moduloAgendas?.crear === true ||
    moduloAgendas?.create === true;

  const puedeActualizar =
    moduloAgendas?.puedeActualizar === true ||
    moduloAgendas?.actualizar === true ||
    moduloAgendas?.editar === true ||
    moduloAgendas?.update === true;

  const puedeEliminar =
    moduloAgendas?.puedeEliminar === true ||
    moduloAgendas?.eliminar === true ||
    moduloAgendas?.delete === true;

  // ==============================
  // VALIDAR SESIÓN
  // ==============================
  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/login");
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  // ==============================
  // FORMATEAR FECHA
  // ==============================
  const formatearFechaSinDesfase = useCallback((fecha) => {
    if (!fecha) return "-";

    try {
      if (typeof fecha === "string" && /^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
        const [year, month, day] = fecha.split("-");
        return `${day}/${month}/${year}`;
      }

      if (typeof fecha === "string" && fecha.includes("T")) {
        const parteFecha = fecha.split("T")[0];

        if (/^\d{4}-\d{2}-\d{2}$/.test(parteFecha)) {
          const [year, month, day] = parteFecha.split("-");
          return `${day}/${month}/${year}`;
        }
      }

      const d = new Date(fecha);

      if (Number.isNaN(d.getTime())) return "-";

      const day = String(d.getUTCDate()).padStart(2, "0");
      const month = String(d.getUTCMonth() + 1).padStart(2, "0");
      const year = d.getUTCFullYear();

      return `${day}/${month}/${year}`;
    } catch {
      return "-";
    }
  }, []);

  // ==============================
  // DESCARGAR EXCEL
  // ==============================
  const descargarAgendaExcel = useCallback(
    async (agendaId) => {
      try {
        setDownloadingId(agendaId);

        const response = await axios.get(
          `/api/agendasviaticos/generar-excel/${agendaId}`,
          {
            ...authConfig(),
            responseType: "blob",
          }
        );

        const contentDisposition = response.headers["content-disposition"];
        let fileName = `AGENDA_${agendaId}.xlsx`;

        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);

          if (match && match[1]) {
            fileName = match[1];
          }
        }

        const url = window.URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );

        const link = document.createElement("a");

        link.href = url;
        link.setAttribute("download", fileName);

        document.body.appendChild(link);

        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error al descargar la agenda:", error);

        if (manejarErrorSesion(error)) return;

        Swal.fire(
          "Error",
          error.response?.data?.message ||
            "No se pudo descargar la agenda en Excel.",
          "error"
        );
      } finally {
        setDownloadingId(null);
      }
    },
    [manejarErrorSesion]
  );

  // ==============================
  // ELIMINAR AGENDA
  // ==============================
  const deleteAgenda = useCallback(
    async (agendaId) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar agendas de viaje.",
          "warning"
        );

        return false;
      }

      try {
        const result = await Swal.fire({
          title: "¿Está seguro?",
          text: "¡No podrá revertir este proceso!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
        });

        if (!result.isConfirmed) return false;

        setDeletingId(agendaId);

        const { data } = await axios.delete(
          `/api/agendasviaticos/delete/${agendaId}`,
          authConfig()
        );

        setAgendas((prev) =>
          prev.filter((agenda) => agenda._id !== agendaId)
        );

        Swal.fire({
          icon: "success",
          title: data?.message || "Agenda eliminada correctamente",
          showConfirmButton: false,
          timer: 1500,
        });

        return true;
      } catch (error) {
        console.error("Error al eliminar la agenda:", error);

        if (manejarErrorSesion(error)) return false;

        Swal.fire(
          "Error",
          error.response?.data?.message || "No se pudo eliminar la agenda.",
          "error"
        );

        return false;
      } finally {
        setDeletingId(null);
      }
    },
    [puedeEliminar, manejarErrorSesion]
  );

  // ==============================
  // DUPLICAR AGENDA
  // ==============================
  const duplicarAgenda = useCallback(
    async (agendaId) => {
      if (!puedeCrear) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para duplicar agendas de viaje.",
          "warning"
        );

        return;
      }

      try {
        const result = await Swal.fire({
          title: "¿Duplicar agenda?",
          text: "Se creará una nueva agenda con la misma información.",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Sí, duplicar",
          cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return;

        setDuplicatingId(agendaId);

        const { data } = await axios.post(
          `/api/agendasviaticos/duplicate/${agendaId}`,
          {},
          authConfig()
        );

        Swal.fire({
          icon: "success",
          title: data?.message || "Agenda duplicada correctamente",
          showConfirmButton: false,
          timer: 1500,
        });

        getData();
      } catch (error) {
        console.error("Error al duplicar agenda:", error);

        if (manejarErrorSesion(error)) return;

        Swal.fire(
          "Error",
          error.response?.data?.message || "No se pudo duplicar la agenda.",
          "error"
        );
      } finally {
        setDuplicatingId(null);
      }
    },
    [puedeCrear, manejarErrorSesion]
  );

  // ==============================
  // CARGAR DATOS
  // ==============================
  const getData = useCallback(async () => {
    try {
      if (!id) return;

      setLoading(true);

      const { data } = await axios.get(
        `/api/agendasviaticos/listbycontrato/${id}`,
        authConfig()
      );

      const lista = Array.isArray(data?.agendas) ? data.agendas : [];

      // ORDEN DESCENDENTE:
      // El último registro agregado queda primero en la tabla.
      const obtenerFechaOrden = (agenda) => {
        if (agenda?.createdAt) {
          const fecha = new Date(agenda.createdAt).getTime();
          if (!Number.isNaN(fecha)) return fecha;
        }

        if (agenda?.fechaCreacion) {
          const fecha = new Date(agenda.fechaCreacion).getTime();
          if (!Number.isNaN(fecha)) return fecha;
        }

        if (agenda?._id && agenda._id.length >= 8) {
          return parseInt(agenda._id.substring(0, 8), 16) * 1000;
        }

        return 0;
      };

      const agendasOrdenadas = [...lista].sort(
        (a, b) => obtenerFechaOrden(b) - obtenerFechaOrden(a)
      );

      setAgendas(agendasOrdenadas);
    } catch (error) {
      console.error("Error al cargar las agendas:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message ||
          "No se pudieron cargar las agendas del contrato.",
        "error"
      );

      setAgendas([]);
    } finally {
      setLoading(false);
    }
  }, [id, manejarErrorSesion]);

  useEffect(() => {
    getData();
  }, [getData, refresh]);

  // ==============================
  // DATA TABLE
  // ==============================
  const dataTabla = useMemo(
    () =>
      agendas.map((agenda, index) => ({
        ...agenda,

        item: index + 1,

        ruta: (
          <div style={{ lineHeight: "1.4" }}>
            {String(agenda.ruta || "")
              .split(".")
              .filter(Boolean)
              .map((item, indexRuta) => (
                <div key={indexRuta} className="small text-dark">
                  • {item.trim()}
                </div>
              ))}
          </div>
        ),

        entidad: (
          <div style={{ lineHeight: "1.4" }}>
            {String(agenda.entidad || "")
              .split(".")
              .filter(Boolean)
              .map((item, indexEntidad) => (
                <div key={indexEntidad} className="small text-dark">
                  • {item.trim()}
                </div>
              ))}
          </div>
        ),

        contactoEntidad: (
          <div style={{ lineHeight: "1.4" }}>
            {String(agenda.contactoEntidad || "")
              .split(".")
              .filter(Boolean)
              .map((item, indexContacto) => (
                <div key={indexContacto} className="small text-dark">
                  • {item.trim()}
                </div>
              ))}
          </div>
        ),

        fechaInicioDesplazamiento: formatearFechaSinDesfase(
          agenda.fechaInicioDesplazamiento
        ),

        fechaFinDesplazamiento: formatearFechaSinDesfase(
          agenda.fechaFinDesplazamiento
        ),

        totalDias: Array.isArray(agenda.agendaDias)
          ? agenda.agendaDias.length
          : 0,

        totalObligaciones: Array.isArray(agenda.obligacionesContractuales)
          ? agenda.obligacionesContractuales.length
          : 0,

        totalGastos: new Intl.NumberFormat("es-CO", {
          style: "currency",
          currency: "COP",
          maximumFractionDigits: 0,
        }).format(
          Number(agenda.gastosaereo || 0) +
            Number(agenda.gastosterrestre || 0) +
            Number(agenda.gastosintermunicipal || 0)
        ),

        funciones: (
          <div className="d-flex gap-2 flex-wrap justify-content-end">
            <button
              className="btn btn-outline-success btn-sm d-inline-flex align-items-center justify-content-center"
              style={{ width: 34, height: 34 }}
              onClick={() => descargarAgendaExcel(agenda._id)}
              title="Descargar agenda en Excel"
              type="button"
              disabled={
                loading ||
                deletingId === agenda._id ||
                duplicatingId === agenda._id ||
                downloadingId === agenda._id
              }
            >
              {downloadingId === agenda._id ? (
                <i className="fa-solid fa-spinner fa-spin"></i>
              ) : (
                <i className="fa-solid fa-download"></i>
              )}
            </button>

            {puedeActualizar && (
              <button
                className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() => navigate(`/instructor/agendas/editar/${agenda._id}`)}
                title="Editar agenda"
                type="button"
                disabled={
                  loading ||
                  deletingId === agenda._id ||
                  duplicatingId === agenda._id ||
                  downloadingId === agenda._id
                }
              >
                <i className="fa-solid fa-pen-to-square"></i>
              </button>
            )}

            {puedeCrear && (
              <button
                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() => duplicarAgenda(agenda._id)}
                title="Duplicar agenda"
                type="button"
                disabled={
                  loading ||
                  deletingId === agenda._id ||
                  duplicatingId === agenda._id ||
                  downloadingId === agenda._id
                }
              >
                {duplicatingId === agenda._id ? (
                  <i className="fa-solid fa-spinner fa-spin"></i>
                ) : (
                  <i className="fa-solid fa-copy"></i>
                )}
              </button>
            )}

            {puedeEliminar && (
              <button
                className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() => deleteAgenda(agenda._id)}
                title="Eliminar agenda"
                type="button"
                disabled={
                  loading ||
                  deletingId === agenda._id ||
                  duplicatingId === agenda._id ||
                  downloadingId === agenda._id
                }
              >
                {deletingId === agenda._id ? (
                  <i className="fa-solid fa-spinner fa-spin text-white"></i>
                ) : (
                  <i className="fa-solid fa-trash"></i>
                )}
              </button>
            )}
          </div>
        ),
      })),
    [
      agendas,
      loading,
      deletingId,
      duplicatingId,
      downloadingId,
      puedeActualizar,
      puedeCrear,
      puedeEliminar,
      navigate,
      deleteAgenda,
      duplicarAgenda,
      descargarAgendaExcel,
      formatearFechaSinDesfase,
    ]
  );

  const columns = useMemo(
    () => [
      { Header: "#", accessor: "item" },
      { Header: "Ruta", accessor: "ruta" },
      { Header: "Entidad", accessor: "entidad" },
      //{ Header: "Contacto", accessor: "contactoEntidad" },
      { Header: "Inicio", accessor: "fechaInicioDesplazamiento" },
      { Header: "Fin", accessor: "fechaFinDesplazamiento" },
      { Header: "Días", accessor: "totalDias" },
      { Header: "Total Gastos", accessor: "totalGastos" },
      { Header: "Acciones", accessor: "funciones" },
    ],
    []
  );

  // ==============================
  // RENDER
  // ==============================
  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Agendas de Viáticos
                </h5>

                <small className="text-secondary">
                  Gestión de agendas de viaje asociadas al contrato.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() => navigate(-1)}
                  title="Volver"
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  disabled={loading}
                  title="Actualizar"
                >
                  <i
                    className={`fa-solid ${
                      loading ? "fa-spinner fa-spin" : "fa-rotate"
                    } me-2`}
                  ></i>
                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    disabled={loading}
                    onClick={() => navigate(`/instructor/agendas/nuevo/${id}`)}
                    title="Nueva Agenda de Viáticos"
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>

                <div className="mt-3">Cargando agendas…</div>
              </div>
            ) : dataTabla.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={dataTabla} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">✈️</div>

                <div className="fw-semibold">
                  No hay agendas de viáticos registradas
                </div>

                <div className="small mb-3">
                  Este contrato aún no tiene agendas asociadas.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() => navigate(`/instructor/agendas/nuevo/${id}`)}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Crear agenda
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarAgendasViatico;